import { Tabs, Tab, Box, Button, Divider } from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import {
  PREDICTION_CATEGORIES,
  CATEGORY_LABELS,
  PredictionCategory,
} from '../../Predictions/constants/categories';
import { PairStatsByCategory } from '../api/usePairMapper';

export const HISTORY_TAB = '__history__' as const;
export type MapperTabValue = PredictionCategory | typeof HISTORY_TAB;

interface Props {
  value: MapperTabValue;
  onChange: (value: MapperTabValue) => void;
  stats?: PairStatsByCategory;
}

const TAB_SX = {
  minHeight: 36,
  py: 0,
  textTransform: 'none' as const,
  fontSize: '0.85rem',
};

// The category tabs select which pair bucket to browse. "Run History" is a
// different KIND of view (matcher runs, not a pair category), so it lives
// outside the scrollable Tabs as its own button — see the user feedback that
// it "seemed like another category" when it was just the last tab.
export const CategoryTabs = ({ value, onChange, stats }: Props) => {
  const isHistory = value === HISTORY_TAB;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        borderBottom: '1px solid rgba(255,255,255,0.1)',
      }}
    >
      <Tabs
        // `false` = no category selected (the History view is active). MUI
        // accepts it and suppresses the "value not in tabs" warning.
        value={isHistory ? false : value}
        onChange={(_, v) => onChange(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          flex: 1,
          minWidth: 0,
          minHeight: 36,
          '& .MuiTabs-indicator': { height: 2 },
        }}
      >
        {PREDICTION_CATEGORIES.map((cat) => {
          const count = stats?.byCategory?.[cat]?.total ?? 0;
          return (
            <Tab
              key={cat}
              value={cat}
              sx={TAB_SX}
              label={
                <Box component="span">
                  {CATEGORY_LABELS[cat]}{' '}
                  <Box
                    component="span"
                    sx={{ opacity: 0.6, fontSize: '0.8em', ml: 0.25 }}
                  >
                    ({count})
                  </Box>
                </Box>
              }
            />
          );
        })}
      </Tabs>

      <Divider
        orientation="vertical"
        flexItem
        sx={{ mx: 1, my: 0.5, borderColor: 'rgba(255,255,255,0.15)' }}
      />

      <Button
        onClick={() => onChange(HISTORY_TAB)}
        startIcon={<HistoryIcon sx={{ fontSize: '1rem' }} />}
        size="small"
        variant={isHistory ? 'contained' : 'outlined'}
        color={isHistory ? 'primary' : 'inherit'}
        sx={{
          textTransform: 'none',
          whiteSpace: 'nowrap',
          fontSize: '0.8rem',
          mr: 1,
          flexShrink: 0,
        }}
      >
        Run History
      </Button>
    </Box>
  );
};
